import React from 'react'
import { Card, CardContent, CardHeader, Select, Button, Table, Thead, Tbody, Tr, Th, Td, Badge } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getOwnerContactsByAccountIds } from '@/modules/accounts/services/ownerContactService'
import { listAllRewardsLinks, updateRewardsLinkStatus } from '../../services/rewardsAdminService'
import type { RewardsAccountExchangeLink } from '../../types'

const STATUS_OPTS: Array<RewardsAccountExchangeLink['status']> = ['awaiting', 'linked', 'not_linked', 'syncing', 'error']

function statusBadge(status: RewardsAccountExchangeLink['status']) {
  const base = { variant: 'soft' as const }
  if (status === 'linked') return <Badge {...base} color="success">linked</Badge>
  if (status === 'awaiting') return <Badge {...base} color="warning">awaiting</Badge>
  if (status === 'syncing') return <Badge {...base} color="info">syncing</Badge>
  if (status === 'error') return <Badge {...base} color="error">error</Badge>
  return <Badge {...base} color="secondary">{status}</Badge>
}

export default function RewardsSettingsLinksCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [savingId, setSavingId] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<RewardsAccountExchangeLink[]>([])
  const [ownerByAccount, setOwnerByAccount] = React.useState<Map<string, any>>(new Map())
  const [search, setSearch] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const data = await listAllRewardsLinks()
      setRows(data)
      const accountIds = Array.from(new Set(data.map((r) => r.account_id)))
      const owners = await getOwnerContactsByAccountIds(accountIds)
      setOwnerByAccount(owners as any)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar vÃ­nculos')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const owner = (ownerByAccount as any).get(r.account_id) || null
      const email = String(owner?.email || '').toLowerCase()
      const name = String(owner?.name || '').toLowerCase()
      return r.account_id.toLowerCase().includes(q) || email.includes(q) || name.includes(q) || r.exchange.toLowerCase().includes(q)
    })
  }, [rows, ownerByAccount, search])

  async function onChangeStatus(id: string, prev: RewardsAccountExchangeLink['status'], next: RewardsAccountExchangeLink['status']) {
    setSavingId(id)
    try {
      const shouldStamp = next === 'linked' && prev !== 'linked'
      const linkedAt = shouldStamp ? new Date().toISOString() : null
      await updateRewardsLinkStatus({ id, status: next, linkedAt })
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status: next, linked_at: linkedAt || r.linked_at }
            : r,
        ),
      )
      toast({ title: 'Rewards - VÃ­nculos', description: 'Status atualizado.', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Rewards - VÃ­nculos', description: e?.message || 'Falha ao atualizar', variant: 'error' })
    } finally {
      setSavingId(null)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Rewards - VÃ­nculos (MASTER)</CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground/70">
          Gerencie status de vinculaÃ§Ã£o (ex.: confirmaÃ§Ã£o manual). Apenas links com status <span className="font-medium text-foreground">linked</span> entram no ciclo.
        </p>

        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <input
            className="h-9 w-full max-w-[420px] rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por e-mail, nome, account_id..."
          />
          <Button variant="outline" onClick={load} disabled={loading || !!savingId}>
            Recarregar
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-foreground/70">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">Sem vÃ­nculos.</div>
        ) : (
          <div className="overflow-auto rounded-md border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Cliente</Th>
                  <Th>Corretora</Th>
                  <Th>UID</Th>
                  <Th>Status</Th>
                  <Th>Alterar</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((r) => {
                  const owner = (ownerByAccount as any).get(r.account_id) || null
                  const label = owner?.email || owner?.name || r.account_id
                  return (
                    <Tr key={r.id}>
                      <Td>
                        <div className="min-w-[220px]">
                          <div className="text-sm font-medium">{label}</div>
                          <div className="text-xs text-foreground/60">{r.account_id}</div>
                        </div>
                      </Td>
                      <Td className="font-medium">{r.exchange.toUpperCase()}</Td>
                      <Td className="font-mono text-xs">{r.exchange_user_id}</Td>
                      <Td>{statusBadge(r.status)}</Td>
                      <Td>
                        <Select
                          value={r.status}
                          onChange={(e) => onChangeStatus(r.id, r.status, (e.target as HTMLSelectElement).value as any)}
                          disabled={!!savingId && savingId === r.id}
                        >
                          {STATUS_OPTS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Select>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}


